import { useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { Field, inp, btn } from "../../lib/ops";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";

const TITLES = { baseline_change: "Ask to change the fixed deadline", exclusion: "Ask to exclude from on-time score", postpone: "Record a customer postponement", prepo_start: "Ask to start production on this Pre-PO" };
const HINT = {
  baseline_change: "The original deadline stays in the history. The new one applies once approved: in Mumbai by the Managing Partner or a founder, in Delhi by the Production Head or a founder.",
  exclusion: "Use only when the delay was outside the factory's control. The step still shows in reports as excluded.",
  postpone: "The customer's new date becomes the deadline once approved. The original date is kept and shown alongside.",
  prepo_start: "A Pre-PO is not a confirmed order. Production can start only after approval, and it won't count as a PO.",
};
const EXTRA = {
  baseline_change: (f) => ({ new_start: f.new_start, new_end: f.new_end }),
  exclusion: (f) => ({ code: f.code }),
  postpone: (f) => ({ new_date: f.new_date }),
};
const COMPLETE = {
  baseline_change: (f) => f.new_start && f.new_end,
  exclusion: (f) => f.code,
  postpone: (f) => f.new_date,
};

const payload = (kind, uid, target, f) => ({ kind, uid, process_id: target?.process_id || null, line_id: target?.line_id || null, reason: f.reason, ...(EXTRA[kind]?.(f) || {}) });
const ready = (kind, f) => f.reason.trim().length >= 3 && (!COMPLETE[kind] || !!COMPLETE[kind](f));

const DateIn = ({ label, k, f, set, testid }) => <Field label={label}><input type="date" data-testid={testid} value={f[k]} onChange={(e) => set(k, e.target.value)} className={inp} /></Field>;

const KindFields = ({ kind, f, set, meta }) => {
  if (kind === "baseline_change") {
    return <div className="grid grid-cols-2 gap-2"><DateIn label="New start" k="new_start" f={f} set={set} testid="request-new-start" /><DateIn label="New deadline" k="new_end" f={f} set={set} testid="request-new-end" /></div>;
  }
  if (kind === "exclusion") {
    return (<Field label="Reason type"><select data-testid="request-code" value={f.code} onChange={(e) => set("code", e.target.value)} className={inp}>
      <option value="">Pick one</option>{Object.entries(meta.exclusion_codes).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></Field>);
  }
  if (kind === "postpone") return <DateIn label="New date agreed with the customer" k="new_date" f={f} set={set} testid="request-new-date" />;
  return null;
};

export const RequestDialog = ({ kind, uid, target, meta, onClose, onDone }) => {
  const [f, setF] = useState({ new_start: target?.baseline_start || "", new_end: target?.baseline_end || "", new_date: target?.commitment_current || "", code: "", reason: "" });
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const send = () => api.post("/ops/requests", payload(kind, uid, target, f))
    .then(() => { toast.success("Sent for approval"); onDone?.(); onClose(); }).catch((e) => toast.error(errMsg(e)));
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md bg-card" data-testid="ops-request-dialog">
        <DialogHeader><DialogTitle>{TITLES[kind]}</DialogTitle></DialogHeader>
        <p className="text-xs text-muted-foreground">{target?.name || target?.site || uid} · {HINT[kind]}</p>
        <div className="space-y-3">
          <KindFields kind={kind} f={f} set={set} meta={meta} />
          <Field label="Why"><textarea data-testid="request-reason" value={f.reason} onChange={(e) => set("reason", e.target.value)} className="w-full bg-muted/60 border p-2 text-sm h-20" placeholder="e.g. Customer approved drawings 4 days late (email 12 Oct)" /></Field>
          <button data-testid="request-submit" disabled={!ready(kind, f)} onClick={send} className={btn}>Send for approval</button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
